<template>
    <jetstreem-modal :show="show" :max-width="'md'" @close="$emit('close')">
        <div class="m-5">
            <h2 class="text-xl font-semibold text-gray-900 text-left">
                Provide Missing Information
            </h2>
            
            <form @submit.prevent="submitMissingRequirements" aria-label="Login Form">
                <div v-if="form.outstanding_requirements.length === 0" class="text-center py-4 text-gray-500">
                    <p>No outstanding requirements found.</p>
                </div>
                <div v-for="req in form.outstanding_requirements" class="text-left mt-4">
                    <div
                        v-if="!['business_address', 'associated_people', 'business_bank_account', 'business_industry', 'business_product_description'].includes(req.name)">
                        <label :for="req.name" class="block text-sm font-medium text-gray-700">
                            {{ formatString(req.name) }} <span v-if="!['address_2'].includes(req.name)" class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input v-if="req.type === 'file'" :id="req.name"
                                @input="req.value = $event.target.files[0]" :type="req.type"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2">
                            
                            <select v-else-if="req.name === 'business_org_type'" :id="req.name" v-model="req.value"
                                
                                :disabled="user_business_type === 'individual'"
                                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100"
                                :class="user_business_type === 'individual' ? 'bg-gray-100' : ''"
                                >
                                <option value="SOLE PROPRIETORSHIP">Sole Proprietorship</option>
                                <option value="LIMITED LIABILITY COMPANY">Limited Liability Company (LLC)</option>
                                <option value="PRIVATE COMPANY">Private Company</option>
                                <option value="PUBLIC COMPANY (UNLISTED)">Public Company (Unlisted)</option>
                                <option value="PUBLIC COMPANY (LISTED)">Public Company (Listed)</option>
                                <option value="CHARITABLE ORGANIZATION / NOT-FOR-PROFIT">Charitable Organization / Non-Profit</option>
                                <option value="PARTNERSHIP">Partnership</option>
                            </select>
                            <select v-else-if="req.name === 'state'" :id="req.name" v-model="req.value"
                                
                                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100"
                                >
                                <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                            </select>
                            <input v-else :id="req.name" v-model="req.value" :type="req.type"
                                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2">
                            <small v-if="req.name === 'business_ein' && user_business_type === 'individual'" class="text-gray-500">Enter your SSN if you
                                don't have
                                a Business EIN.</small>
                            <small v-if="req.name === 'business_website' && user_business_type === 'individual'" class="text-gray-500">
                                Enter https://rentrisk.com if you do not have a website.
                            </small>
                            <small v-if="req.name === 'business_name' && user_business_type === 'individual'" class="text-gray-500">
                                Enter your full name if you do not have a business name.
                            </small>
                            <p v-text="form.errors[req.name]" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                </div>
                <div class="pt-5">
                    <div class="flex justify-end">
                        <button type="button" @click="$emit('close')"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Close</button>

                        <form-button :form="form" type="submit" class="ml-2">
                            Save
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </jetstreem-modal>
</template>

<script>
import JetstreemModal from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';

export default {
    components: {
        JetstreemModal,
        FormButton,
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        originator: {
            type: Object,
            default: null
        },
        user_business_type: {
            type: String,
            default: 'individual'
        },
        states: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            form: this.$inertia.form({
                outstanding_requirements: [],
            }),
        }
    },
    watch: {
        originator: {
            handler() {
                this.initializeOutstandingRequirements();
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        formatString(input) {
            return input
                .replace(/_/g, ' ') // Replace underscores with spaces
                .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
        },

        submitMissingRequirements() {
            this.form.post(this.route('plaid.onboarding.missing-requirements.store'), {
                onSuccess: () => {
                    this.$emit('close');
                }
            })
        },

        initializeOutstandingRequirements() {
            if (this.originator && this.originator.outstanding_requirements && this.originator.outstanding_requirements.length) {
                // Clear the form first
                this.form.outstanding_requirements = [];
                
                this.originator.outstanding_requirements.forEach((req) => {
                    let type = 'text';
                    let documentTypesNames = [
                        "ssn_card",
                        "driver_license_or_id",
                        "personal_address_validation",
                        "business_address_validation",
                        "irs_document",
                        "business_name_validation"
                    ];
                    const reqType = (req.requirement_type || '').toLowerCase();
                    if (documentTypesNames.includes(reqType)) {
                        type = 'file';
                    }
                    var value = null;
                    if(reqType === 'business_org_type' && this.user_business_type === 'individual') {
                        value = 'SOLE PROPRIETORSHIP';
                    }
                    if(reqType === 'business_address') {
                        this.form.outstanding_requirements.push({ name: 'address_1', type: type, value: value });
                        this.form.outstanding_requirements.push({ name: 'address_2', type: type, value: value });
                        this.form.outstanding_requirements.push({ name: 'city', type: type, value: value });
                        this.form.outstanding_requirements.push({ name: 'state', type: type, value: value });
                        this.form.outstanding_requirements.push({ name: 'postal_code', type: type, value: value });
                    } else {
                        // include person_id when provided by Plaid for person-specific requirements
                        this.form.outstanding_requirements.push({ name: reqType, type: type, value: value, person_id: req.person_id || null });
                    }
                })
            }
        }
    }
}
</script>
